import React, { useState } from "react";
import { useFormikContext } from "formik";
import visa from "../../../../../../../images/v2/visa24x24.svg";
import mastercard from "../../../../../../../images/v2/master24x24.svg";
import discover from "../../../../../../../images/v2/discover24x24.svg";
import amex from "../../../../../../../images/v2/amex24x24.svg";
import TextInput from "../../../../../components/form/text-input";
import Switch from "../../../../../components/form/switch";
import FancyRadioInput from "../../../../../components/form/fancy-radio-input";
import useUserStore from "../../../../../stores/user";
import useOrganizationStore from "../../../../../stores/organization";
import PinBasedDebit from "../components/pin-based-debit";
import { getSoftLimitWarning } from "../../../utils/soft-limit";
import styles from "./style.module.scss";
import Collapsible from "../../../../../components/collapsible";
import SlidingToggle from "../../../../../components/form/sliding-checkbox";

const Tiered = () => {
  const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
    useFormikContext();
  const user = useUserStore((state) => state.user);
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const nonEditableTemplateOrPricingRestricted =
    values.nonEditableTemplateApplied ||
    user?.organization?.pricing_restricted === 1;

  const [backup, setBackup] = useState({
    tiered_amex_flat_rate: values?.tiered_amex_flat_rate,
    tiered_amex_flat_transaction: values?.tiered_amex_flat_transaction,
    tiered_amex_ic_plus_rate: values?.tiered_amex_ic_plus_rate,
    tiered_amex_ic_plus_transaction: values?.tiered_amex_ic_plus_transaction,
    amex_tier_one_rate: values?.amex_tier_one_rate,
    amex_tier_one_fee: values?.amex_tier_one_fee,
    amex_tier_two_rate: values?.amex_tier_two_rate,
    amex_tier_two_fee: values?.amex_tier_two_fee,
    amex_tier_three_rate: values?.amex_tier_three_rate,
    amex_tier_three_fee: values?.amex_tier_three_fee,
    amex_tier_four_rate: values?.amex_tier_four_rate,
    amex_tier_four_fee: values?.amex_tier_four_fee,
    tiered_amex_structure: values?.tiered_amex_structure,
  });

  const fieldGroups = {
    amex: [
      "tiered_amex_flat_rate",
      "tiered_amex_flat_transaction",
      "tiered_amex_ic_plus_rate",
      "tiered_amex_ic_plus_transaction",
      "amex_tier_one_rate",
      "amex_tier_one_fee",
      "amex_tier_two_rate",
      "amex_tier_two_fee",
      "amex_tier_three_rate",
      "amex_tier_three_fee",
      "amex_tier_four_rate",
      "amex_tier_four_fee",
    ],
  };

  const handleSwitchChangeBlur = (e, type) => {

    type === "change" && handleChange(e);
    type === "blur" && handleBlur(e);

    if (!e.target.checked) {
      fieldGroups['amex'].forEach((field) => {
        setFieldValue(field, backup[field]);
      });
    }
  };

  return (
    <div className={styles.container}>

      <div className={styles.radioItem}>
        <p className={styles.p_heading}>
          Choose your Tiered Pricing Type
        </p>
        <div className={styles.radios}>
          <SlidingToggle
            options={["swiped", "keyed"]}
            width="150px"
            value={values.tiered_type}
            onChange={(values) =>
              handleChange({
                target: { name: "tiered_type", value: values },
              })
            }
            disabled={nonEditableTemplateOrPricingRestricted}
          />
        </div>
        {errors.tiered_type && touched.tiered_type && (
          <p className={styles.error}>{errors.tiered_type}</p>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.header}>
          <h6>Qualified</h6>
          <div className={styles.accepts}>
            <img src={visa} />
            <img src={mastercard} />
            <img src={discover} />
          </div>
        </div>

        <div className={styles.body}>
          <TextInput
            label={"Qual Rate"}
            placeholder={"0.00"}
            suffix={"%"}
            type="text"
            inputMode="decimal"
            id={"tiered_qual_rate"}
            name={"tiered_qual_rate"}
            value={values.tiered_qual_rate}
            onBlur={handleBlur}
            touched={touched?.tiered_qual_rate}
            errored={errors?.tiered_qual_rate}
            error={errors?.tiered_qual_rate}
            hasWarning={getSoftLimitWarning(
              pricingDefaults?.tiered?.tiered_qual_rate?.soft_limit,
              values.tiered_qual_rate,
              errors.tiered_qual_rate,
            )}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralIntegerScale: 4,
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: {
                    name: "tiered_qual_rate",
                    value: e.target.rawValue,
                  },
                });
              },
            }}
            disabled={nonEditableTemplateOrPricingRestricted}
          />
          <TextInput
            label={"Qual Txn Fee"}
            placeholder={"0.00"}
            prefix={"$"}
            inputMode="decimal"
            id={"tiered_qual_transaction"}
            name={"tiered_qual_transaction"}
            value={values.tiered_qual_transaction}
            onBlur={handleBlur}
            touched={touched?.tiered_qual_transaction}
            errored={errors?.tiered_qual_transaction}
            error={errors?.tiered_qual_transaction}
            hasWarning={getSoftLimitWarning(
              pricingDefaults?.tiered?.tiered_qual_transaction?.soft_limit,
              values.tiered_qual_transaction,
              errors.tiered_qual_transaction,
            )}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralIntegerScale: 4,
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: {
                    name: "tiered_qual_transaction",
                    value: e.target.rawValue,
                  },
                });
              },
            }}
            disabled={nonEditableTemplateOrPricingRestricted}
          />
          <TextInput
            label={"Qual Debit Rate"}
            placeholder={"0.00"}
            suffix={"%"}
            type="text"
            inputMode="decimal"
            id={"tiered_qual_debit_rate"}
            name={"tiered_qual_debit_rate"}
            value={values.tiered_qual_debit_rate}
            onBlur={handleBlur}
            touched={touched?.tiered_qual_debit_rate}
            errored={errors?.tiered_qual_debit_rate}
            error={errors?.tiered_qual_debit_rate}
            hasWarning={getSoftLimitWarning(
              pricingDefaults?.tiered?.tiered_qual_debit_rate?.soft_limit,
              values.tiered_qual_debit_rate,
              errors.tiered_qual_debit_rate,
            )}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralIntegerScale: 4,
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: {
                    name: "tiered_qual_debit_rate",
                    value: e.target.rawValue,
                  },
                });
              },
            }}
            disabled={nonEditableTemplateOrPricingRestricted}
          />
          <TextInput
            label={"Qual Debit Txn Fee"}
            placeholder={"0.00"}
            prefix={"$"}
            inputMode="decimal"
            id={"tiered_qual_debit_per_transaction"}
            name={"tiered_qual_debit_per_transaction"}
            value={values.tiered_qual_debit_per_transaction}
            onBlur={handleBlur}
            touched={touched?.tiered_qual_debit_per_transaction}
            errored={errors?.tiered_qual_debit_per_transaction}
            error={errors?.tiered_qual_debit_per_transaction}
            hasWarning={getSoftLimitWarning(
              pricingDefaults?.tiered?.tiered_qual_debit_per_transaction
                ?.soft_limit,
              values.tiered_qual_debit_per_transaction,
              errors.tiered_qual_debit_per_transaction,
            )}
            cleaveOptions={{
              numeral: true,
              numeralDecimalScale: 2,
              numeralIntegerScale: 4,
              numeralPositiveOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: {
                    name: "tiered_qual_debit_per_transaction",
                    value: e.target.rawValue,
                  },
                });
              },
            }}
            disabled={nonEditableTemplateOrPricingRestricted}
          />
        </div>
      </div>

      <Collapsible title="More Tiered Rates">
        <div className={styles.card}>
          <div className={styles.header}>
            <h6>Mid-Qualified</h6>
            <div className={styles.accepts}>
              <img src={visa} />
              <img src={mastercard} />
              <img src={discover} />
            </div>
          </div>

          <div className={styles.body}>
            <TextInput
              label={"Mid-Qual Rate"}
              placeholder={"0.00"}
              suffix={"%"}
              type="text"
              inputMode="decimal"
              id={"tiered_mid_qual_rate"}
              name={"tiered_mid_qual_rate"}
              value={values.tiered_mid_qual_rate}
              onBlur={handleBlur}
              touched={touched?.tiered_mid_qual_rate}
              errored={errors?.tiered_mid_qual_rate}
              error={errors?.tiered_mid_qual_rate}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_mid_qual_rate?.soft_limit,
                values.tiered_mid_qual_rate,
                errors.tiered_mid_qual_rate,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_mid_qual_rate",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
            <TextInput
              label={"Mid-Qual Txn Fee"}
              placeholder={"0.00"}
              prefix={"$"}
              inputMode="decimal"
              id={"tiered_mid_qual_transaction"}
              name={"tiered_mid_qual_transaction"}
              value={values.tiered_mid_qual_transaction}
              onBlur={handleBlur}
              touched={touched?.tiered_mid_qual_transaction}
              errored={errors?.tiered_mid_qual_transaction}
              error={errors?.tiered_mid_qual_transaction}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_mid_qual_transaction?.soft_limit,
                values.tiered_mid_qual_transaction,
                errors.tiered_mid_qual_transaction,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_mid_qual_transaction",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
            <TextInput
              label={"Mid-Qual Debit Rate"}
              placeholder={"0.00"}
              suffix={"%"}
              type="text"
              inputMode="decimal"
              id={"tiered_mid_qual_debit_rate"}
              name={"tiered_mid_qual_debit_rate"}
              value={values.tiered_mid_qual_debit_rate}
              onBlur={handleBlur}
              touched={touched?.tiered_mid_qual_debit_rate}
              errored={errors?.tiered_mid_qual_debit_rate}
              error={errors?.tiered_mid_qual_debit_rate}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_mid_qual_debit_rate?.soft_limit,
                values.tiered_mid_qual_debit_rate,
                errors.tiered_mid_qual_debit_rate,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_mid_qual_debit_rate",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
            <TextInput
              label={"Mid-Qual Debit Txn Fee"}
              placeholder={"0.00"}
              prefix={"$"}
              inputMode="decimal"
              id={"tiered_mid_qual_debit_per_transaction"}
              name={"tiered_mid_qual_debit_per_transaction"}
              value={values.tiered_mid_qual_debit_per_transaction}
              onBlur={handleBlur}
              touched={touched?.tiered_mid_qual_debit_per_transaction}
              errored={errors?.tiered_mid_qual_debit_per_transaction}
              error={errors?.tiered_mid_qual_debit_per_transaction}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_mid_qual_debit_per_transaction
                  ?.soft_limit,
                values.tiered_mid_qual_debit_per_transaction,
                errors.tiered_mid_qual_debit_per_transaction,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_mid_qual_debit_per_transaction",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.header}>
            <h6>non-Qualified</h6>
            <div className={styles.accepts}>
              <img src={visa} />
              <img src={mastercard} />
              <img src={discover} />
            </div>
          </div>

          <div className={styles.body}>
            <TextInput
              label={"Non-Qual Rate"}
              placeholder={"0.00"}
              suffix={"%"}
              type="text"
              inputMode="decimal"
              id={"tiered_non_qual_rate"}
              name={"tiered_non_qual_rate"}
              value={values.tiered_non_qual_rate}
              onBlur={handleBlur}
              touched={touched?.tiered_non_qual_rate}
              errored={errors?.tiered_non_qual_rate}
              error={errors?.tiered_non_qual_rate}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_non_qual_rate?.soft_limit,
                values.tiered_non_qual_rate,
                errors.tiered_non_qual_rate,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_non_qual_rate",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
            <TextInput
              label={"Non-Qual Txn Fee"}
              placeholder={"0.00"}
              prefix={"$"}
              type="text"
              inputMode="decimal"
              id={"tiered_non_qual_transaction"}
              name={"tiered_non_qual_transaction"}
              value={values.tiered_non_qual_transaction}
              onBlur={handleBlur}
              touched={touched?.tiered_non_qual_transaction}
              errored={errors?.tiered_non_qual_transaction}
              error={errors?.tiered_non_qual_transaction}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_non_qual_transaction?.soft_limit,
                values.tiered_non_qual_transaction,
                errors.tiered_non_qual_transaction,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_non_qual_transaction",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
            <TextInput
              label={"Non-Qual Debit Rate"}
              placeholder={"0.00"}
              suffix={"%"}
              type="text"
              id={"tiered_non_qual_debit_rate"}
              name={"tiered_non_qual_debit_rate"}
              value={values.tiered_non_qual_debit_rate}
              onBlur={handleBlur}
              touched={touched?.tiered_non_qual_debit_rate}
              errored={errors?.tiered_non_qual_debit_rate}
              error={errors?.tiered_non_qual_debit_rate}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_non_qual_debit_rate?.soft_limit,
                values.tiered_non_qual_debit_rate,
                errors.tiered_non_qual_debit_rate,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_non_qual_debit_rate",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
            />
            <TextInput
              label={"Non-Qual Debit Txn Fee"}
              placeholder={"0.00"}
              prefix={"$"}
              type="text"
              id={"tiered_non_qual_debit_per_transaction"}
              name={"tiered_non_qual_debit_per_transaction"}
              value={values.tiered_non_qual_debit_per_transaction}
              onBlur={handleBlur}
              touched={touched?.tiered_non_qual_debit_per_transaction}
              errored={errors?.tiered_non_qual_debit_per_transaction}
              error={errors?.tiered_non_qual_debit_per_transaction}
              hasWarning={getSoftLimitWarning(
                pricingDefaults?.tiered?.tiered_non_qual_debit_per_transaction
                  ?.soft_limit,
                values.tiered_non_qual_debit_per_transaction,
                errors.tiered_non_qual_debit_per_transaction,
              )}
              cleaveOptions={{
                numeral: true,
                numeralDecimalScale: 2,
                numeralIntegerScale: 4,
                numeralPositiveOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "tiered_non_qual_debit_per_transaction",
                      value: e.target.rawValue,
                    },
                  });
                },
              }}
              disabled={nonEditableTemplateOrPricingRestricted}
            />
          </div>
        </div>
      </Collapsible>


      <div className={styles.amexCard}>
        <div className={styles.amexHeader}>
          <h6>Amex structure</h6>
          <img src={amex} />
        </div>
        <Switch
          id={'tiered_amex_toggle'}
          name={'tiered_amex_toggle'}
          label="Enable Amex pricing"
          checked={values?.tiered_amex_toggle === "yes"}
          onChange={(e) => {
            handleSwitchChangeBlur(e, 'change');
            setFieldValue('tiered_amex_toggle', e.target.checked ? "yes" : "no");
            if (!e.target.checked) {
              setFieldValue("tiered_amex_flat_rate", 0);
              setFieldValue("tiered_amex_flat_transaction", 0);
              setFieldValue("tiered_amex_ic_plus_rate", 0);
              setFieldValue("tiered_amex_ic_plus_transaction", 0);
              setFieldValue("amex_tier_one_rate", 0);
              setFieldValue("amex_tier_one_fee", 0);
              setFieldValue("tiered_amex_structure", "");
            } else {
              setFieldValue("tiered_amex_flat_rate", backup.tiered_amex_flat_rate);
              setFieldValue("tiered_amex_flat_transaction", backup.tiered_amex_flat_transaction);
              setFieldValue("tiered_amex_ic_plus_rate", backup.tiered_amex_ic_plus_rate);
              setFieldValue("tiered_amex_ic_plus_transaction", backup.tiered_amex_ic_plus_transaction);
              setFieldValue("amex_tier_one_rate", backup.amex_tier_one_rate);
              setFieldValue("amex_tier_one_fee", backup.amex_tier_one_fee);
              setFieldValue("tiered_amex_structure", backup.tiered_amex_structure);
            }
          }}
          onBlur={(e) => {
            handleSwitchChangeBlur(e, 'blur');
            setFieldValue('tiered_amex_toggle', e.target.checked ? "yes" : "no");
            if (!e.target.checked) {
              setFieldValue("tiered_amex_flat_rate", 0);
              setFieldValue("tiered_amex_flat_transaction", 0);
              setFieldValue("tiered_amex_ic_plus_rate", 0);
              setFieldValue("tiered_amex_ic_plus_transaction", 0);
              setFieldValue("amex_tier_one_rate", 0);
              setFieldValue("amex_tier_one_fee", 0);
              setFieldValue("tiered_amex_structure", "");
            } else {
              setFieldValue("tiered_amex_flat_rate", backup.tiered_amex_flat_rate);
              setFieldValue("tiered_amex_flat_transaction", backup.tiered_amex_flat_transaction);
              setFieldValue("tiered_amex_ic_plus_rate", backup.tiered_amex_ic_plus_rate);
              setFieldValue("tiered_amex_ic_plus_transaction", backup.tiered_amex_ic_plus_transaction);
              setFieldValue("amex_tier_one_rate", backup.amex_tier_one_rate);
              setFieldValue("amex_tier_one_fee", backup.amex_tier_one_fee);
              setFieldValue("tiered_amex_structure", backup.tiered_amex_structure);
            }
          }}
          disabled={nonEditableTemplateOrPricingRestricted}
        />

        {values?.tiered_amex_toggle === 'yes' && (
          <>
            <p>Select preferred AMEX Structure</p>

            <div className={styles.radioBoxes}>
              <FancyRadioInput
                label={"Flat Rate"}
                id={"amex_flat_rate"}
                name={"tiered_amex_structure"}
                value={"flat_rate"}
                className={styles.radioButton}
                defaultChecked={values?.tiered_amex_structure === "flat_rate"}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "tiered_amex_structure",
                      value: e.target.value,
                    },
                  });
                  setBackup(prev => ({
                    ...prev,
                    tiered_amex_structure: e.target.value,
                  }));
                }}
                disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
              />
              <FancyRadioInput
                label={"IC+"}
                id={"amex_ic_plus"}
                name={"tiered_amex_structure"}
                value={"ic_plus"}
                className={styles.radioButton}
                defaultChecked={values?.tiered_amex_structure === "ic_plus"}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "tiered_amex_structure",
                      value: e.target.value,
                    },
                  });
                  setBackup(prev => ({
                    ...prev,
                    tiered_amex_structure: e.target.value,
                  }));
                }}
                disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
              />
              <FancyRadioInput
                label={"Tiered"}
                id={"amex_tiered"}
                name={"tiered_amex_structure"}
                value={"tiered"}
                className={styles.radioButton}
                defaultChecked={values?.tiered_amex_structure === "tiered"}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "tiered_amex_structure",
                      value: e.target.value,
                    },
                  });
                  setBackup(prev => ({
                    ...prev,
                    tiered_amex_structure: e.target.value,
                  }));
                }}
                disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
              />
            </div>

            {values?.tiered_amex_structure === "flat_rate" && (
              <div className={styles.card}>
                <div className={styles.header}>
                  <h6>Amex flat rate</h6>
                </div>

                <div className={`${styles.body} ${styles.gridCol2}`}>
                  <TextInput
                    label={"AMEX Flat Rate"}
                    placeholder={"0.00"}
                    suffix={"%"}
                    type="text"
                    id={"tiered_amex_flat_rate"}
                    name={"tiered_amex_flat_rate"}
                    value={values.tiered_amex_flat_rate}
                    onBlur={handleBlur}
                    touched={touched?.tiered_amex_flat_rate}
                    errored={errors?.tiered_amex_flat_rate}
                    error={errors?.tiered_amex_flat_rate}
                    hasWarning={getSoftLimitWarning(
                      pricingDefaults?.tiered?.tiered_amex_flat_rate?.soft_limit,
                      values.tiered_amex_flat_rate,
                      errors.tiered_amex_flat_rate,
                    )}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "tiered_amex_flat_rate",
                            value: e.target.rawValue,
                          },
                        });
                        setBackup(prev => ({
                          ...prev,
                          tiered_amex_flat_rate: e.target.rawValue,
                        }));
                      },
                    }}
                    disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                  />
                  <TextInput
                    label={"AMEX Flat Rate Txn Fee"}
                    placeholder={"0.00"}
                    prefix={"$"}
                    type="text"
                    id={"tiered_amex_flat_transaction"}
                    name={"tiered_amex_flat_transaction"}
                    value={values.tiered_amex_flat_transaction}
                    onBlur={handleBlur}
                    touched={touched?.tiered_amex_flat_transaction}
                    errored={errors?.tiered_amex_flat_transaction}
                    error={errors?.tiered_amex_flat_transaction}
                    hasWarning={getSoftLimitWarning(
                      pricingDefaults?.tiered?.tiered_amex_flat_transaction
                        ?.soft_limit,
                      values.tiered_amex_flat_transaction,
                      errors.tiered_amex_flat_transaction,
                    )}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "tiered_amex_flat_transaction",
                            value: e.target.rawValue,
                          },
                        });
                        setBackup({
                          ...backup,
                          tiered_amex_flat_transaction: e.target.rawValue,
                        });
                      },
                    }}
                    disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                  />
                </div>
              </div>
            )}

            {values?.tiered_amex_structure === "ic_plus" && (
              <div className={styles.card}>
                <div className={styles.header}>
                  <h6>Amex Interchange rate</h6>
                </div>

                <div className={`${styles.body} ${styles.gridCol2}`}>
                  <TextInput
                    label={"AMEX IC+ Rate"}
                    placeholder={"0.00"}
                    suffix={"%"}
                    type="text"
                    id={"tiered_amex_ic_plus_rate"}
                    name={"tiered_amex_ic_plus_rate"}
                    value={values.tiered_amex_ic_plus_rate}
                    onBlur={handleBlur}
                    touched={touched?.tiered_amex_ic_plus_rate}
                    errored={errors?.tiered_amex_ic_plus_rate}
                    error={errors?.tiered_amex_ic_plus_rate}
                    hasWarning={getSoftLimitWarning(
                      pricingDefaults?.tiered?.tiered_amex_ic_plus_rate?.soft_limit,
                      values.tiered_amex_ic_plus_rate,
                      errors.tiered_amex_ic_plus_rate,
                    )}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "tiered_amex_ic_plus_rate",
                            value: e.target.rawValue,
                          },
                        });
                        setBackup(prev => ({
                          ...prev,
                          tiered_amex_ic_plus_rate: e.target.rawValue,
                        }));
                      },
                    }}
                    disabled={values?.tiered_amex_toggle === 'no'}
                  />
                  <TextInput
                    label={"AMEX IC+ Txn Fee"}
                    placeholder={"0.00"}
                    prefix={"$"}
                    type="text"
                    id={"tiered_amex_ic_plus_transaction"}
                    name={"tiered_amex_ic_plus_transaction"}
                    value={values.tiered_amex_ic_plus_transaction}
                    onBlur={handleBlur}
                    touched={touched?.tiered_amex_ic_plus_transaction}
                    errored={errors?.tiered_amex_ic_plus_transaction}
                    error={errors?.tiered_amex_ic_plus_transaction}
                    hasWarning={getSoftLimitWarning(
                      pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction
                        ?.soft_limit,
                      values.tiered_amex_ic_plus_transaction,
                      errors.tiered_amex_ic_plus_transaction,
                    )}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "tiered_amex_ic_plus_transaction",
                            value: e.target.rawValue,
                          },
                        });
                        setBackup(prev => ({
                          ...prev,
                          tiered_amex_ic_plus_transaction: e.target.rawValue,
                        }));
                      },
                    }}
                    disabled={values?.tiered_amex_toggle === 'no'}
                  />
                </div>
              </div>
            )}

            {values?.tiered_amex_structure === "tiered" && (
              <div className={`${styles.body} ${styles.bodyTieredAmex}`}>
                <div className={styles.card}>
                  <div className={styles.header}>
                    <h6>Amex Tier 1</h6>
                  </div>

                  <div className={`${styles.body} ${styles.gridCol2}`}>
                    <TextInput
                      label={"AMEX Tier 1 Rate"}
                      placeholder={"0.00"}
                      suffix={"%"}
                      type="text"
                      inputMode="decimal"
                      id={"amex_tier_one_rate"}
                      name={"amex_tier_one_rate"}
                      value={values.amex_tier_one_rate}
                      onBlur={handleBlur}
                      touched={touched?.amex_tier_one_rate}
                      errored={errors?.amex_tier_one_rate}
                      error={errors?.amex_tier_one_rate}
                      hasWarning={getSoftLimitWarning(
                        pricingDefaults?.tiered?.amex_tier_one_rate?.soft_limit,
                        values.amex_tier_one_rate,
                        errors.amex_tier_one_rate,
                      )}
                      cleaveOptions={{
                        numeral: true,
                        numeralDecimalScale: 2,
                        numeralIntegerScale: 4,
                        numeralPositiveOnly: true,
                        onValueChanged: (e) => {
                          handleChange({
                            target: {
                              name: "amex_tier_one_rate",
                              value: e.target.rawValue,
                            },
                          });
                          setBackup(prev => ({
                            ...prev,
                            amex_tier_one_rate: e.target.rawValue,
                          }));
                        },
                      }}
                      disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                    />
                    <TextInput
                      label={"AMEX Tier 1 Txn Fee"}
                      placeholder={"0.00"}
                      prefix={"$"}
                      type="text"
                      inputMode="decimal"
                      id={"amex_tier_one_fee"}
                      name={"amex_tier_one_fee"}
                      value={values.amex_tier_one_fee}
                      onBlur={handleBlur}
                      touched={touched?.amex_tier_one_fee}
                      errored={errors?.amex_tier_one_fee}
                      error={errors?.amex_tier_one_fee}
                      hasWarning={getSoftLimitWarning(
                        pricingDefaults?.tiered?.amex_tier_one_fee?.soft_limit,
                        values.amex_tier_one_fee,
                        errors.amex_tier_one_fee,
                      )}
                      cleaveOptions={{
                        numeral: true,
                        numeralDecimalScale: 2,
                        numeralIntegerScale: 4,
                        numeralPositiveOnly: true,
                        onValueChanged: (e) => {
                          handleChange({
                            target: {
                              name: "amex_tier_one_fee",
                              value: e.target.rawValue,
                            },
                          });
                          setBackup(prev => ({
                            ...prev,
                            amex_tier_one_fee: e.target.rawValue,
                          }));
                        },
                      }}
                      disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                    />
                  </div>
                </div>


                <Collapsible title="More Amex Tiered Rates">
                  <div className={styles.card}>
                    <div className={styles.header}>
                      <h6>Amex Tier 2</h6>
                    </div>

                    <div className={`${styles.body} ${styles.gridCol2}`}>
                      <TextInput
                        label={"AMEX Tier 2 Rate"}
                        placeholder={"0.00"}
                        suffix={"%"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_two_rate"}
                        name={"amex_tier_two_rate"}
                        value={values.amex_tier_two_rate}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_two_rate}
                        errored={errors?.amex_tier_two_rate}
                        error={errors?.amex_tier_two_rate}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_two_rate?.soft_limit,
                          values.amex_tier_two_rate,
                          errors.amex_tier_two_rate,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_two_rate",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_two_rate: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                      <TextInput
                        label={"AMEX Tier 2 Txn Fee"}
                        placeholder={"0.00"}
                        prefix={"$"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_two_fee"}
                        name={"amex_tier_two_fee"}
                        value={values.amex_tier_two_fee}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_two_fee}
                        errored={errors?.amex_tier_two_fee}
                        error={errors?.amex_tier_two_fee}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_two_fee?.soft_limit,
                          values.amex_tier_two_fee,
                          errors.amex_tier_two_fee,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_two_fee",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_two_fee: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                    </div>
                  </div>

                  <div className={styles.card}>
                    <div className={styles.header}>
                      <h6>Amex Tier 3</h6>
                    </div>

                    <div className={`${styles.body} ${styles.gridCol2}`}>
                      <TextInput
                        label={"AMEX Tier 3 Rate"}
                        placeholder={"0.00"}
                        suffix={"%"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_three_rate"}
                        name={"amex_tier_three_rate"}
                        value={values.amex_tier_three_rate}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_three_rate}
                        errored={errors?.amex_tier_three_rate}
                        error={errors?.amex_tier_three_rate}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_three_rate?.soft_limit,
                          values.amex_tier_three_rate,
                          errors.amex_tier_three_rate,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_three_rate",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_three_rate: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                      <TextInput
                        label={"AMEX Tier 3 Txn Fee"}
                        placeholder={"0.00"}
                        prefix={"$"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_three_fee"}
                        name={"amex_tier_three_fee"}
                        value={values.amex_tier_three_fee}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_three_fee}
                        errored={errors?.amex_tier_three_fee}
                        error={errors?.amex_tier_three_fee}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_three_fee?.soft_limit,
                          values.amex_tier_three_fee,
                          errors.amex_tier_three_fee,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_three_fee",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_three_fee: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                    </div>
                  </div>

                  <div className={styles.card}>
                    <div className={styles.header}>
                      <h6>Amex Tier 4</h6>
                    </div>

                    <div className={`${styles.body} ${styles.gridCol2}`}>
                      <TextInput
                        label={"AMEX Tier 4 Rate"}
                        placeholder={"0.00"}
                        suffix={"%"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_four_rate"}
                        name={"amex_tier_four_rate"}
                        value={values.amex_tier_four_rate}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_four_rate}
                        errored={errors?.amex_tier_four_rate}
                        error={errors?.amex_tier_four_rate}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_four_rate?.soft_limit,
                          values.amex_tier_four_rate,
                          errors.amex_tier_four_rate,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_four_rate",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_four_rate: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                      <TextInput
                        label={"AMEX Tier 4 Txn Fee"}
                        placeholder={"0.00"}
                        prefix={"$"}
                        type="text"
                        inputMode="decimal"
                        id={"amex_tier_four_fee"}
                        name={"amex_tier_four_fee"}
                        value={values.amex_tier_four_fee}
                        onBlur={handleBlur}
                        touched={touched?.amex_tier_four_fee}
                        errored={errors?.amex_tier_four_fee}
                        error={errors?.amex_tier_four_fee}
                        hasWarning={getSoftLimitWarning(
                          pricingDefaults?.tiered?.amex_tier_four_fee?.soft_limit,
                          values.amex_tier_four_fee,
                          errors.amex_tier_four_fee,
                        )}
                        cleaveOptions={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralIntegerScale: 4,
                          numeralPositiveOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: {
                                name: "amex_tier_four_fee",
                                value: e.target.rawValue,
                              },
                            });
                            setBackup(prev => ({
                              ...prev,
                              amex_tier_four_fee: e.target.rawValue,
                            }));
                          },
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted || values?.tiered_amex_toggle === 'no'}
                      />
                    </div>
                  </div>
                </Collapsible>

              </div>
            )}
          </>
        )}
      </div>

      <PinBasedDebit pricingModel="tiered" noMarginTop />
    </div>
  );
};

export default Tiered;
